/// <reference types="google.maps" />

/**
 * Подключение Google Maps (Maps JavaScript API).
 *
 * Ключ задаётся переменной VITE_GOOGLE_MAPS_API_KEY при сборке сайта. В Google
 * Cloud у ключа должно быть ограничение по HTTP Referer (адреса сайта) и
 * включён Maps JavaScript API; без привязанного платёжного аккаунта Google
 * карту не отдаёт.
 *
 * Скрипт грузится по требованию — только когда открыта телеметрия: каждая
 * загрузка карты расходует бесплатную месячную квоту.
 */

export const GOOGLE_MAPS_KEY: string | undefined =
  import.meta.env.VITE_GOOGLE_MAPS_API_KEY || undefined;

/** Имя глобальной функции, которую Google вызывает после загрузки скрипта. */
const READY_CALLBACK = '__tmsGoogleMapsReady';

/**
 * Промис загрузки один на всё приложение: карта монтируется и на вкладке,
 * и в панели трека, а второй тег скрипта Google считает ошибкой.
 */
let loader: Promise<typeof google.maps> | null = null;

/**
 * Подписчики на отказ ключа. Google сообщает о нём не ошибкой загрузки
 * (скрипт при этом загружается успешно), а вызовом глобальной функции
 * gm_authFailure — уже после того, как карта создана и показала серый экран.
 */
const authFailureListeners = new Set<(reason: string) => void>();

const AUTH_FAILURE_REASON =
  'Google отклонил ключ карт: проверьте в Google Cloud, что включён Maps JavaScript API, ' +
  'к проекту привязан платёжный аккаунт, а в ограничениях ключа разрешён адрес этого сайта.';

let authFailed = false;

/** Сообщить, когда Google отклонит ключ. Возвращает отписку. */
export function onGoogleAuthFailure(listener: (reason: string) => void): () => void {
  if (authFailed) {
    listener(AUTH_FAILURE_REASON);
    return () => undefined;
  }
  authFailureListeners.add(listener);
  return () => authFailureListeners.delete(listener);
}

export function loadGoogleMaps(): Promise<typeof google.maps> {
  if (loader) return loader;

  if (!GOOGLE_MAPS_KEY) {
    return Promise.reject(new Error('Ключ Google Maps не задан'));
  }
  if (authFailed) {
    return Promise.reject(new Error(AUTH_FAILURE_REASON));
  }

  const globals = window as unknown as Record<string, unknown>;

  globals.gm_authFailure = () => {
    authFailed = true;
    for (const listener of authFailureListeners) listener(AUTH_FAILURE_REASON);
    authFailureListeners.clear();
  };

  loader = new Promise((resolve, reject) => {
    const existing = (window as unknown as { google?: typeof google }).google?.maps;
    if (existing?.Map) {
      resolve(existing);
      return;
    }

    globals[READY_CALLBACK] = () => {
      const maps = (window as unknown as { google?: typeof google }).google?.maps;
      if (maps?.Map) resolve(maps);
      else reject(new Error('Скрипт Google Maps загрузился, но API недоступен'));
    };

    const script = document.createElement('script');
    const params = new URLSearchParams({
      key: GOOGLE_MAPS_KEY,
      v: 'weekly',
      language: 'ru',
      region: 'UZ',
      callback: READY_CALLBACK,
    });
    script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
    script.async = true;

    script.onerror = () => {
      // Сбрасываем промис: в закрытой сети попытка может стать удачной после
      // того, как администратор откроет доступ к maps.googleapis.com.
      loader = null;
      reject(
        new Error(
          'Не удалось загрузить Google Maps: нет доступа к maps.googleapis.com ' +
            'или ключ отклонён. Проверьте ключ в Google Cloud.',
        ),
      );
    };

    document.head.appendChild(script);
  });

  return loader;
}
